import { useMemo, useState } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import type { FaqCategoryViewModel, FaqDto } from '@/api/types/faqHost';
import { ApiError } from '@/api/client/abpError';
import { FaqHostPermissions } from './permissions';
import { CategoryFormModal } from './CategoryFormModal';
import type { CategoryFormValues } from './CategoryFormModal';
import { FaqFormModal } from './FaqFormModal';
import type { FaqFormValues } from './FaqFormModal';
import { EditCategoryFeaturesModal } from './EditCategoryFeaturesModal';
import {
  useCreateCategory,
  useCreateFaq,
  useDeleteCategory,
  useDeleteFaq,
  useFaqList,
  useUpdateCategory,
  useUpdateCategoryFeatures,
  useUpdateFaq,
} from './api';

function stripHtml(html: string): string {
  const el = document.createElement('div');
  el.innerHTML = html;
  return (el.textContent || '').toLowerCase();
}

type CategoryModalState =
  | { mode: 'create' }
  | { mode: 'edit'; id: string; title: string }
  | null;

type FaqModalState =
  | { mode: 'create'; categoryId: string }
  | { mode: 'edit'; id: string; question: string; answer: string }
  | null;

type DeleteState =
  | { kind: 'category'; id: string; name: string }
  | { kind: 'faq'; id: string }
  | null;

export function FaqHostPage() {
  const can = usePermission();
  const canCreateCategory = can(FaqHostPermissions.createCategory);
  const canEditCategory = can(FaqHostPermissions.editCategory);
  const canDeleteCategory = can(FaqHostPermissions.deleteCategory);
  const canCreateFaq = can(FaqHostPermissions.createFaq);
  const canEditFaq = can(FaqHostPermissions.editFaq);
  const canDeleteFaq = can(FaqHostPermissions.deleteFaq);
  const canEditFeatures = can(FaqHostPermissions.editCategoryFeatures);

  const toast = useToast();
  const listQuery = useFaqList();
  const createCategoryMut = useCreateCategory();
  const updateCategoryMut = useUpdateCategory();
  const deleteCategoryMut = useDeleteCategory();
  const updateFeaturesMut = useUpdateCategoryFeatures();
  const createFaqMut = useCreateFaq();
  const updateFaqMut = useUpdateFaq();
  const deleteFaqMut = useDeleteFaq();

  const [search, setSearch] = useState('');
  const [categoryModal, setCategoryModal] = useState<CategoryModalState>(null);
  const [faqModal, setFaqModal] = useState<FaqModalState>(null);
  const [featuresTarget, setFeaturesTarget] = useState<{ id: string; title: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteState>(null);

  const categories = listQuery.data ?? [];

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return categories;
    return categories
      .map((c) => {
        const categoryMatches = c.title.toLowerCase().includes(term);
        if (categoryMatches) return c;
        const faqs = c.faqs.filter(
          (f) => f.question.toLowerCase().includes(term) || stripHtml(f.answer).includes(term),
        );
        return faqs.length > 0 ? { ...c, faqs } : null;
      })
      .filter((c): c is FaqCategoryViewModel => c !== null);
  }, [categories, search]);

  function reportError(err: unknown, fallback: string) {
    toast.error(err instanceof ApiError ? err.message : fallback);
  }

  function handleCategorySubmit(values: CategoryFormValues) {
    if (!categoryModal) return;
    const input = { title: values.title.trim() };
    if (categoryModal.mode === 'create') {
      createCategoryMut.mutate(input, {
        onSuccess: () => {
          toast.success('Category added.');
          setCategoryModal(null);
        },
        onError: (err) => reportError(err, 'Failed to add the category.'),
      });
    } else {
      updateCategoryMut.mutate(
        { id: categoryModal.id, input },
        {
          onSuccess: () => {
            toast.success('Category updated.');
            setCategoryModal(null);
          },
          onError: (err) => reportError(err, 'Failed to update the category.'),
        },
      );
    }
  }

  function handleFaqSubmit(values: FaqFormValues) {
    if (!faqModal) return;
    if (faqModal.mode === 'create') {
      createFaqMut.mutate(
        {
          categoryId: faqModal.categoryId,
          question: values.question.trim(),
          answer: values.answer,
        },
        {
          onSuccess: () => {
            toast.success('FAQ added.');
            setFaqModal(null);
          },
          onError: (err) => reportError(err, 'Failed to add the FAQ.'),
        },
      );
    } else {
      updateFaqMut.mutate(
        { id: faqModal.id, input: { question: values.question.trim(), answer: values.answer } },
        {
          onSuccess: () => {
            toast.success('FAQ updated.');
            setFaqModal(null);
          },
          onError: (err) => reportError(err, 'Failed to update the FAQ.'),
        },
      );
    }
  }

  function handleFeaturesSubmit(features: string[]) {
    if (!featuresTarget) return;
    updateFeaturesMut.mutate(
      { categoryId: featuresTarget.id, features },
      {
        onSuccess: () => {
          toast.success('Category features updated.');
          setFeaturesTarget(null);
        },
        onError: (err) => reportError(err, 'Failed to update the category features.'),
      },
    );
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    if (deleteTarget.kind === 'category') {
      deleteCategoryMut.mutate(deleteTarget.id, {
        onSuccess: () => {
          toast.success('Category deleted.');
          setDeleteTarget(null);
        },
        onError: (err) => {
          reportError(err, 'Failed to delete the category.');
          setDeleteTarget(null);
        },
      });
    } else {
      deleteFaqMut.mutate(deleteTarget.id, {
        onSuccess: () => {
          toast.success('FAQ deleted.');
          setDeleteTarget(null);
        },
        onError: (err) => {
          reportError(err, 'Failed to delete the FAQ.');
          setDeleteTarget(null);
        },
      });
    }
  }

  if (listQuery.isLoading) return <Spinner />;
  if (listQuery.isError)
    return <ErrorState message="Could not load the FAQ." onRetry={() => listQuery.refetch()} />;

  return (
    <div>
      <PageHeader
        title="Frequently Asked Questions"
        breadcrumb={['Home', 'FAQ']}
        actions={
          canCreateCategory ? (
            <button type="button" className="btn btn-primary" onClick={() => setCategoryModal({ mode: 'create' })}>
              <i className="fa fa-plus me-1" />
              Add Category
            </button>
          ) : undefined
        }
      />

      <div className="mb-3">
        <input
          type="search"
          className="form-control"
          placeholder="Search FAQs…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState message="No categories found." />
      ) : (
        filtered.map((category) => (
          <CategoryCard
            key={category.id}
            category={category}
            canEditCategory={canEditCategory}
            canDeleteCategory={canDeleteCategory}
            canCreateFaq={canCreateFaq}
            canEditFaq={canEditFaq}
            canDeleteFaq={canDeleteFaq}
            canEditFeatures={canEditFeatures}
            onEditCategory={() => setCategoryModal({ mode: 'edit', id: category.id, title: category.title })}
            onDeleteCategory={() =>
              setDeleteTarget({ kind: 'category', id: category.id, name: category.title })
            }
            onEditFeatures={() => setFeaturesTarget({ id: category.id, title: category.title })}
            onAddFaq={() => setFaqModal({ mode: 'create', categoryId: category.id })}
            onEditFaq={(faq) =>
              setFaqModal({ mode: 'edit', id: faq.id, question: faq.question, answer: faq.answer })
            }
            onDeleteFaq={(faq) => setDeleteTarget({ kind: 'faq', id: faq.id })}
          />
        ))
      )}

      {categoryModal && (
        <CategoryFormModal
          show
          mode={categoryModal.mode}
          initial={categoryModal.mode === 'edit' ? { title: categoryModal.title } : undefined}
          busy={createCategoryMut.isPending || updateCategoryMut.isPending}
          onSubmit={handleCategorySubmit}
          onClose={() => setCategoryModal(null)}
        />
      )}

      {faqModal && (
        <FaqFormModal
          show
          mode={faqModal.mode}
          initial={
            faqModal.mode === 'edit'
              ? { question: faqModal.question, answer: faqModal.answer }
              : undefined
          }
          busy={createFaqMut.isPending || updateFaqMut.isPending}
          onSubmit={handleFaqSubmit}
          onClose={() => setFaqModal(null)}
        />
      )}

      <EditCategoryFeaturesModal
        categoryId={featuresTarget?.id ?? null}
        categoryTitle={featuresTarget?.title ?? ''}
        busy={updateFeaturesMut.isPending}
        onSubmit={handleFeaturesSubmit}
        onClose={() => setFeaturesTarget(null)}
      />

      <ConfirmDialog
        show={deleteTarget !== null}
        title={deleteTarget?.kind === 'category' ? 'Delete category' : 'Delete FAQ'}
        message={
          deleteTarget?.kind === 'category'
            ? `Are you sure you want to delete the category "${deleteTarget.name}"?`
            : 'Are you sure you want to delete this FAQ?'
        }
        confirmLabel="Delete"
        busy={deleteCategoryMut.isPending || deleteFaqMut.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

interface CategoryCardProps {
  category: FaqCategoryViewModel;
  canEditCategory: boolean;
  canDeleteCategory: boolean;
  canCreateFaq: boolean;
  canEditFaq: boolean;
  canDeleteFaq: boolean;
  canEditFeatures: boolean;
  onEditCategory: () => void;
  onDeleteCategory: () => void;
  onEditFeatures: () => void;
  onAddFaq: () => void;
  onEditFaq: (faq: FaqDto) => void;
  onDeleteFaq: (faq: FaqDto) => void;
}

function CategoryCard({
  category,
  canEditCategory,
  canDeleteCategory,
  canCreateFaq,
  canEditFaq,
  canDeleteFaq,
  canEditFeatures,
  onEditCategory,
  onDeleteCategory,
  onEditFeatures,
  onAddFaq,
  onEditFaq,
  onDeleteFaq,
}: CategoryCardProps) {
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div className="card shadow-sm border-0 category-card mb-3">
      <div className="card-header bg-light d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <h5 className="fw-semibold mb-0">
            {category.title}
            {category.featureDisplayNames.length > 0 && (
              <span className="text-muted"> – {category.featureDisplayNames.join(', ')}</span>
            )}
          </h5>
          <span className="badge bg-primary">{category.faqs.length} FAQs</span>
        </div>
        <div className="d-inline-flex gap-1">
          {canEditFeatures && (
            <button type="button" className="btn btn-light btn-sm" title="Category Features" onClick={onEditFeatures}>
              <i className="fa fa-list-ul" />
            </button>
          )}
          {canCreateFaq && (
            <button type="button" className="btn btn-outline-primary btn-sm" title="Add FAQ" onClick={onAddFaq}>
              <i className="fa fa-plus" />
            </button>
          )}
          {canEditCategory && (
            <button type="button" className="btn btn-outline-warning btn-sm" title="Edit" onClick={onEditCategory}>
              <i className="fa fa-pencil" />
            </button>
          )}
          {canDeleteCategory && (
            <button type="button" className="btn btn-outline-danger btn-sm" title="Delete" onClick={onDeleteCategory}>
              <i className="fa fa-trash" />
            </button>
          )}
        </div>
      </div>
      <div className="card-body p-0">
        <div className="accordion accordion-flush">
          {category.faqs.map((faq) => {
            const open = openId === faq.id;
            return (
              <div className="accordion-item border-0 faq-item" key={faq.id}>
                <h3 className="accordion-header d-flex align-items-center justify-content-between">
                  <button
                    type="button"
                    className={`accordion-button${open ? '' : ' collapsed'}`}
                    onClick={() => setOpenId(open ? null : faq.id)}
                  >
                    {faq.question}
                  </button>
                  <div className="d-inline-flex gap-1 me-2">
                    {canEditFaq && (
                      <button type="button" className="btn btn-outline-warning btn-sm" title="Edit" onClick={() => onEditFaq(faq)}>
                        <i className="fa fa-pencil" />
                      </button>
                    )}
                    {canDeleteFaq && (
                      <button type="button" className="btn btn-outline-danger btn-sm" title="Delete" onClick={() => onDeleteFaq(faq)}>
                        <i className="fa fa-trash" />
                      </button>
                    )}
                  </div>
                </h3>
                {open && (
                  <div className="accordion-collapse collapse show">
                    <div className="accordion-body faq-answer pb-3">
                      {/* Answer is HTML authored in the legacy Quill editor (Html.Raw). */}
                      <div className="p-3 mt-1" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
